import {
  CreditCardOutlined,
  MailOutlined,
  PhoneOutlined,
  ScheduleOutlined,
  UserOutlined,
  DollarCircleOutlined,
} from "@ant-design/icons";
import { Badge, Divider, Drawer, Flex, Timeline, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { bookingStatusTone } from "components/bookings/bookingStatusTone";
import { getCustomerPackageById } from "hooks/queries/customerpackage";
import { activityLogByBookingIdV2Options } from "hooks/queries/booking-v2";
import { useGetUserById } from "hooks/queries/user";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { translate, translateWithVars } from "shared/languages";
import { BookingDto } from "shared/types";
import { BookingStatus, UserType } from "shared/types/enum";
import { getActualRemainingCredits, getBookingStatusLabel, getExpiryDateBadgeStatus, packageActivation } from "shared/utils";
import { formatPhone } from "shared/utils/phone";
import { DEFAULT_DATE_FORMAT, formatDate, tzDate } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

const { Text, Title } = Typography;

// One booking's full detail panel — client + package/credit info plus the
// full activity timeline (creation + every status transition, including
// system/cron-driven ones such as the auto-complete sweep, the waitlist
// sweep, and waitlist auto-promotion, surfaced as "Sistem" by the backend
// when no human actor initiated the change). Opened by a row click from
// ScheduleBooking.tsx and ScheduleWaitlist.tsx, and built from data this app
// already has elsewhere (no invented booking-ref numbers or unrelated
// payment fields).

// Only the fields the drawer reads, so a waitlist row (ScheduleWaitlist.tsx)
// can open it as well as a booking row.
const CREDIT_HELD_STATUSES: BookingStatus[] = [BookingStatus.Pending, BookingStatus.Confirmed, BookingStatus.Waitlist];
const CREDIT_USED_STATUSES: BookingStatus[] = [BookingStatus.Completed, BookingStatus.NoShow];
const creditCostKey = (status: BookingStatus) =>
  CREDIT_HELD_STATUSES.includes(status)
    ? "booking_detail.credit_cost_held"
    : CREDIT_USED_STATUSES.includes(status)
      ? "booking_detail.credit_cost"
      : "booking_detail.credit_cost_returned";

export type BookingDetailDrawerBooking = Pick<
  BookingDto,
  | "id"
  | "customer_id"
  | "customer_package_id"
  | "customer_package_name"
  | "customer_name"
  | "customer_phone"
  | "booking_ref"
  | "booking_status"
  | "class_name"
  | "credit_cost"
>;

const BookingDetailDrawer = ({
  booking,
  onClose,
}: {
  booking: BookingDetailDrawerBooking | null;
  onClose: () => void;
}) => {
  const { data: customer } = useGetUserById(booking?.customer_id ?? "", UserType.Customer);
  const { data: customerPackage } = getCustomerPackageById(booking?.customer_package_id);
  const { data: logs = [], isLoading } = useQuery({
    ...activityLogByBookingIdV2Options(booking?.id),
    enabled: !!booking?.id,
  });

  if (!booking) {
    return <Drawer open={false} onClose={onClose} width={520} destroyOnClose />;
  }

  const remainingCredits = customerPackage ? getActualRemainingCredits(customerPackage) : undefined;
  const isUnlimited = customerPackage?.package_deal.unlimited;

  const eventText = (log: (typeof logs)[number]) =>
    log.old_status === null
      ? `${log.actor_name} ${translate("booking_event.created")} (${getBookingStatusLabel(log.new_status)}).`
      : `${log.actor_name} ${translate("booking_event.status_changed_from")} ${getBookingStatusLabel(log.old_status)} ${translate("booking_event.status_changed_to")} ${getBookingStatusLabel(log.new_status)}.`;

  return (
    <Drawer
      title={translate("title.booking_detail")}
      open={!!booking}
      onClose={onClose}
      width={520}
      destroyOnClose
    >
      <Flex vertical gap={4} style={{ marginBottom: 20 }}>
        {/* The booking code is the headline. Labelled "Kode Booking" to match
            the column header everywhere else — one name for one concept, no
            second "Ref" vocabulary. Legacy bookings that predate the
            booking_ref column fall back to the class/package name instead. */}
        <Title level={4} style={{ margin: 0, fontFamily: "var(--font-display)", letterSpacing: "-0.01em" }}>
          {booking.booking_ref
            ? `${translate("label.booking_code")}: ${booking.booking_ref}`
            : (booking.class_name ?? booking.customer_package_name)}
        </Title>
        <StatusBadge
          tone={bookingStatusTone(booking.booking_status)}
          label={getBookingStatusLabel(booking.booking_status)}
        />
      </Flex>

      <Text strong style={{ display: "block", marginBottom: 10 }}>
        {translate("title.client")}
      </Text>
      <Flex vertical gap={8} style={{ marginBottom: 20 }}>
        <Flex gap={6} align="center">
          <UserOutlined style={{ fontSize: textPx(16) }} />
          <Text>{customer?.full_name ?? booking.customer_name}</Text>
        </Flex>
        <Flex gap={6} align="center">
          <PhoneOutlined style={{ fontSize: textPx(16) }} />
          <Text>{formatPhone(customer?.phone_number ?? booking.customer_phone)}</Text>
        </Flex>
        {customer?.email && (
          <Flex gap={6} align="center">
            <MailOutlined style={{ fontSize: textPx(16) }} />
            <Text>{customer.email}</Text>
          </Flex>
        )}
      </Flex>

      <Divider style={{ margin: "20px 0" }} />

      <Text strong style={{ display: "block", marginBottom: 10 }}>
        {translate("package")}
      </Text>
      <Flex vertical gap={8} style={{ marginBottom: 20 }}>
        <Flex gap={6} align="center">
          <DollarCircleOutlined style={{ fontSize: textPx(16) }} />
          <Text>
            {booking.customer_package_name}
            {customerPackage &&
              " — " +
                (isUnlimited
                  ? translate("customer_package.unlimited")
                  : `${remainingCredits}/${customerPackage.total_credits}`)}
          </Text>
        </Flex>
        {/* What this booking took (its own snapshot), worded by where the
            credits are now: held (Pending, Confirmed, a waitlist spot), used
            (Completed, No Show) or returned (Cancelled, Refunded, an expired
            spot). A membership takes none, so the line is left out there. */}
        {booking.credit_cost !== undefined && customerPackage && !isUnlimited && (
          <Flex gap={6} align="center">
            <CreditCardOutlined style={{ fontSize: textPx(16) }} />
            <Text>
              {translateWithVars(creditCostKey(booking.booking_status as BookingStatus), { n: booking.credit_cost })}
            </Text>
          </Flex>
        )}
        {customerPackage?.expiry_date && (
          <Flex gap={10} align="center" wrap="wrap">
            <ScheduleOutlined style={{ fontSize: textPx(16) }} />
            {packageActivation(customerPackage).unactivated ? (
              // Clock not started: no expiry yet, say when it starts on its own.
              translateWithVars("label.auto_start_on", {
                date: formatDate(packageActivation(customerPackage).autoStartDate!, "DD MMM YYYY"),
              })
            ) : (
              <>
                {translate("label.expiry_date")}:
                <Badge
                  status={getExpiryDateBadgeStatus(tzDate(customerPackage.expiry_date).endOf("day"))}
                  text={formatDate(customerPackage.expiry_date)}
                />
              </>
            )}
          </Flex>
        )}
      </Flex>

      <Divider style={{ margin: "20px 0" }} />

      <Text strong style={{ display: "block", marginBottom: 16 }}>
        {translate("title.booking_activity")}
      </Text>
      <Timeline
        items={logs.map((log) => ({
          children: (
            <Flex vertical gap={2}>
              <Text>{eventText(log)}</Text>
              <Text type="secondary" style={{ fontSize: textPx(12) }}>
                {formatDate(log.created_at, DEFAULT_DATE_FORMAT + ", HH:mm")}
              </Text>
            </Flex>
          ),
        }))}
      />
      {!isLoading && logs.length === 0 && (
        <Text type="secondary">{translate("messages.booking_activity_empty")}</Text>
      )}
    </Drawer>
  );
};

export default BookingDetailDrawer;
